import React, { useEffect, useState, useRef, useMemo } from "react";
import dnPopup from "./utils/dnPopup";
import { FaPaperclip } from "react-icons/fa";
import { apiFetch, API_BASE } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { isWeekend } from "./utils/workweek";
import "./My_leave_requests.css";
import "react-datepicker/dist/react-datepicker.css";
import { de } from "date-fns/locale";

// "1 day" / "2 days" / "0.5 days" — 1 is singular, everything else (including
// half-days) is plural.
const fmtDays = (n) => `${Number(n)} ${Number(n) === 1 ? "day" : "days"}`;
const isAre = (n) => (Number(n) === 1 ? "is" : "are");

// Themed react-select styling (matches the Holiday "Holiday For" dropdown):
// rounded, slate border at rest, orange focus ring, blue selected option.
const SELECT_STYLES = {
    control: (p, s) => ({
        ...p,
        minHeight: 42,
        borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
        fontSize: 14,
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
        padding: "10px 12px",
        cursor: "pointer",
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
};
const BREAKDOWN_OPTIONS = [
    { value: "Full", label: "Full" },
    { value: "First half", label: "First half" },
    { value: "Second half", label: "Second half" },
];

function MyLeaveRequests({ navSize }) {
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [requests, setRequests] = useState([]);
    const [formData, setFormData] = useState({
        leave_type_id: "",
        start_date: "",
        start_date_breakdown: "Full",
        end_date: "",
        end_date_breakdown: "Full",
        attachment: "",
        leave_description: "",
    });
    const [file, setFile] = useState(null);
    const [editId, setEditId] = useState(null);
    const fileInputRef = useRef(null);
    const [leaveBalance, setLeaveBalance] = useState([]);
    const [leaveHistory, setLeaveHistory] = useState([]);
    const [approver, setApprover] = useState("");
    const [holidayDates, setHolidayDates] = useState([]);

    // Fetch all leave types + existing leave requests
    useEffect(() => {
        fetchApprover();
        fetchLeaveTypes();
        fetchLeaveBalance();
        fetchLeaveHistory();
        fetchRequests();
        fetchHolidays();
        setFormData((prev) => ({ ...prev }));
    }, []);

    const fetchHolidays = async (month, year) => {
        month = month || new Date().getMonth() + 1;
        year = year || new Date().getFullYear();
        try {
            const res = await apiFetch(`/api/holiday/list?month=${month}&year=${year}`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            const dates = [];
            (Array.isArray(data) ? data : []).forEach(h => {
                const start = new Date(h.holiday_date);
                const end = h.holiday_end ? new Date(h.holiday_end) : start;
                let cur = new Date(start);
                while (cur <= end) {
                    dates.push(new Date(cur).toDateString());
                    cur.setDate(cur.getDate() + 1);
                }
            });

            // Merge with already-loaded months (dedup) so navigating the
            // datepicker to other months keeps every month's holidays marked.
            setHolidayDates(prev => Array.from(new Set([...prev, ...dates])));
        } catch (err) {
            console.error("Fetch Holidays Error:", err);
        }
    };

    const fetchApprover = async () => {
        try {
            const res = await apiFetch(`/api/Leave/leave_approver`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setApprover(data.approver);
        } catch (err) {
            console.error("❌ Fetch approver error:", err);
        }
    };

    const fetchLeaveBalance = async () => {
        try {
            const res = await apiFetch(`/api/Leave/my_leave_balance`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setLeaveBalance(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Fetch balance error:", err);
        }
    };

    const fetchLeaveHistory = async () => {
        try {
            const res = await apiFetch(`/api/Leave/my_leave_history`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setLeaveHistory(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Fetch history error:", err);
        }
    };

    const fetchLeaveTypes = async () => {
        try {
            const res = await apiFetch("/api/Leave/leave_type_list", {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setLeaveTypes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Fetch leave types error:", err);
        }
    };

    const fetchRequests = async () => {
        try {
            const res = await apiFetch(`/api/Leave/my_leave_request_list`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setRequests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Fetch requests error:", err);
        }
    };

    const renderDayContents = (day, date) => {
        const isHoliday = holidayDates.includes(date.toDateString());

        if (isHoliday) {
            return (
                <span style={{
                    background: "#ff4d4d",
                    color: "#fff",
                    borderRadius: "50%",
                    padding: "4px",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    paddingTop: "2px",
                    position: "relative",
                    top: "-3px"
                }}>
                    H
                </span>
            );
        }

        return <span>{day}</span>;
    };

    const handleFileChange = (e) => setFile(e.target.files[0]);

    const [removeOldAttachment, setRemoveOldAttachment] = useState(false);

    const handleRemoveOldAttachment = () => {
        setFormData({ ...formData, old_attachment: "" });
        setRemoveOldAttachment(true);
    };

    const handleCancelEdit = () => {
        setFormData({
            leave_type_id: "",
            start_date: "",
            start_date_breakdown: "Full",
            end_date: "",
            end_date_breakdown: "Full",
            leave_description: "",
            old_attachment: "",
        });
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = ""; // ✅ clears file input UI
        setEditId(null);
        setRemoveOldAttachment(false);
    };

    const handleSave = async () => {
        const { leave_type_id, start_date, end_date } = formData;

        // Report only the field(s) actually missing
        const missing = [];
        if (!leave_type_id) missing.push("Leave type");
        if (!start_date) missing.push("Start date");
        if (!end_date) missing.push("End date");

        if (missing.length) {
            dnPopup.fire(
                "Validation Error",
                `${missing.join(", ")} ${missing.length > 1 ? "are" : "is"} required.`,
                "error"
            );
            return;
        }

        // ✅ 1. Calculate leave days being applied, excluding holidays that
        //       apply to THIS employee (holidayDates is already targeted via /list).
        const start = new Date(start_date);
        const end = new Date(end_date);
        let appliedDays = 0;
        for (let cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
            // Weekends (Sat/Sun) and the employee's holidays don't count as leave.
            if (!isWeekend(cur) && !holidayDates.includes(cur.toDateString())) appliedDays++;
        }
        // Half-day breakdowns: a First/Second half start/end day counts as 0.5.
        // (Start/End can't be a weekend or holiday — those are disabled in the picker.)
        if (formData.start_date_breakdown && formData.start_date_breakdown !== "Full") appliedDays -= 0.5;
        if (start_date !== end_date && formData.end_date_breakdown && formData.end_date_breakdown !== "Full") appliedDays -= 0.5;

        // ✅ 2. Fetch user's leave balance
        try {
            const res = await apiFetch(`/api/Leave/my_leave_balance`, {
                method: 'GET',
                credentials: 'include',
            });
            const balances = await res.json();

            // ✅ 3. Find selected leave type's balance
            const selectedLeave = balances.find(b => b.leave_type_id === parseInt(leave_type_id));

            if (!selectedLeave) {
                dnPopup.fire("Error", "Unable to fetch your leave balance", "error");
                return;
            }

            // Year-scoped, approved-only balance — the same number payroll uses
            // to decide paid vs unpaid. Falls back to the legacy lifetime figure
            // if the year fields aren't available.
            const remaining = Number(
                selectedLeave.remaining_days_year ?? selectedLeave.balance_leave
            );

            // ✅ 4. Running low on balance is a WARNING, not a wall.
            //
            // Someone with 12 days of Sick Leave may genuinely need 20 for a
            // medical reason — refusing the request doesn't make the need go
            // away, it just stops it being recorded. The excess is unpaid
            // (Loss of Pay); tell them exactly how much and let them decide.
            if (appliedDays > remaining) {
                const unpaid = Math.round((appliedDays - Math.max(0, remaining)) * 100) / 100;
                const ok = await dnPopup.confirm({
                    title: "Part of this leave will be unpaid",
                    text:
                        `You have ${fmtDays(Math.max(0, remaining))} of ${selectedLeave.leave_type} left this year, ` +
                        `and this request is ${fmtDays(appliedDays)}.\n\n` +
                        `${fmtDays(unpaid)} ${isAre(unpaid)} treated as Loss of Pay (unpaid) if approved.\n\n` +
                        `Do you want to submit it anyway?`,
                    confirmLabel: "Submit anyway",
                    cancelLabel: "Go back",
                });
                if (!ok) return;
            }

            // ✅ 5. Submit
            const formDataObj = new FormData();
            Object.entries(formData).forEach(([key, value]) => formDataObj.append(key, value));
            if (file) formDataObj.append("attachment", file);
            if (removeOldAttachment) formDataObj.append("removeOldAttachment", "true");

            const url = editId
                ? `/api/Leave/update_my_leave_request/${editId}`
                : "/api/Leave/add_my_leave_request";
            const method = editId ? "PUT" : "POST";

            const saveRes = await apiFetch(url, {
                method,
                body: formDataObj,
                // Note: do NOT set Content-Type for FormData — the browser adds the
                // correct multipart/form-data boundary automatically.
                credentials: 'include',
            });
            const data = await saveRes.json();

            if (data.success) {
                dnPopup.fire("Success", data.message, "success");
                handleCancelEdit();
                fetchRequests();
            } else {
                dnPopup.fire("Error", data.message || "Something went wrong", "error");
            }

        } catch (err) {
            console.error("Error checking leave balance:", err);
            dnPopup.fire("Error", "Failed to check leave balance", "error");
        }
    };

    const handleEdit = (item) => {
        setFormData({
            leave_type_id: item.leave_type_id,
            start_date: formatDate(item.start_date),
            start_date_breakdown: item.start_date_breakdown,
            end_date: formatDate(item.end_date),
            end_date_breakdown: item.end_date_breakdown,
            leave_description: item.leave_description,
            old_attachment: item.attachment || "", // ✅ store old file path
        });

        setFile(null); // clear file input
        if (fileInputRef.current) fileInputRef.current.value = ""; // ✅ clears file input UI
        setEditId(item.leave_requests_id);
    };

    const handleDelete = async (id) => {
        const confirm = await dnPopup.fire({
            title: "Are you sure?",
            text: "This will delete your leave request!",
            icon: "warning",
            showCancelButton: true,
        });

        if (confirm.isConfirmed) {
            try {
                const res = await apiFetch(`/api/Leave/delete_my_leave_request/${id}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    credentials: 'include',
                });
                const data = await res.json();
                if (data.success) {
                    dnPopup.fire("Deleted!", data.message, "success");
                    fetchRequests();
                }
            } catch {
                dnPopup.fire("Error", "Something went wrong", "error");
            }
        }
    };

    const handleCancel = async (id) => {
        const confirm = await dnPopup.fire({
            title: "Are you sure?",
            text: "Do you want to cancel this leave request?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, cancel it!",
        });

        if (confirm.isConfirmed) {
            try {
                const res = await apiFetch(`/api/Leave/cancel_my_leave_request/${id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    credentials: 'include',
                });
                const data = await res.json();

                if (data.success) {
                    dnPopup.fire("Canceled!", data.message, "success");
                    fetchRequests(); // refresh table
                } else {
                    dnPopup.fire("Error", data.error || "Something went wrong", "error");
                }
            } catch (err) {
                dnPopup.fire("Error", "Something went wrong", "error");
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        // Already a plain YYYY-MM-DD? return as-is (no timezone shift).
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        // Otherwise use LOCAL parts — toISOString() would shift a day back in +UTC zones (e.g. IST).
        const date = new Date(dateString);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    // Stored as "<timestamp>__<original name>" — show only the original name.
    const attachmentName = (p) => {
        const base = String(p || "").split(/[\\/]/).pop();
        const i = base.indexOf("__");
        return i >= 0 ? base.slice(i + 2) : base;
    };

    // Dates already covered by an ACTIVE (Pending/Approved) leave request are
    // blocked — a new request on those dates is only allowed if the existing one
    // is Canceled or Rejected. Editing excludes the request's own dates.
    const bookedDates = useMemo(() => {
        const set = new Set();
        (requests || []).forEach((r) => {
            const status = r.leave_status || "Pending";
            if (status === "Canceled" || status === "Rejected") return;
            if (editId && r.leave_requests_id === editId) return;
            if (!r.start_date || !r.end_date) return;
            const [ys, ms, ds] = String(r.start_date).slice(0, 10).split("-").map(Number);
            const [ye, me, de] = String(r.end_date).slice(0, 10).split("-").map(Number);
            if (!ys || !ye) return;
            let cur = new Date(ys, ms - 1, ds);
            const end = new Date(ye, me - 1, de);
            while (cur <= end) {
                set.add(cur.toDateString());
                cur.setDate(cur.getDate() + 1);
            }
        });
        return set;
    }, [requests, editId]);

    // Single-day leave: start & end are the same day, so the End Date Breakdown
    // must equal the Start Date Breakdown. Keep them synced and lock the end control.
    const isSingleDay = !!formData.start_date && formData.start_date === formData.end_date;

    useEffect(() => {
        if (isSingleDay && formData.end_date_breakdown !== formData.start_date_breakdown) {
            setFormData((prev) => ({ ...prev, end_date_breakdown: prev.start_date_breakdown }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSingleDay, formData.start_date_breakdown, formData.end_date_breakdown]);

    // Format a Date as YYYY-MM-DD using its LOCAL parts. Using toISOString()
    // here would shift the date a day back in +UTC timezones (e.g. IST).
    const toLocalDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    // Earliest selectable date: one month before today (so backdated leave
    // within the last month is allowed for both Start and End dates).
    const oneMonthAgo = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d;
    };

    return (
        <div className="leave-config-container">
            <div className={`leave-config-dashboard ${navSize}`}>
                <div className="leave-header-row">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        My Leave Requests
                    </h2>
                    <div className="approval-info">
                        <strong>Approver: </strong> {approver || "Admin"}
                    </div>
                </div>
                <div className="leave-config-content">
                    <div className="leave-form">
                        <div className="form-group">
                            <label>Leave Type <span style={{ color: "red" }}>*</span></label>
                            <Select
                                options={leaveTypes.map((lt) => ({ value: lt.leave_type_id, label: lt.leave_type }))}
                                value={leaveTypes
                                    .filter((lt) => String(lt.leave_type_id) === String(formData.leave_type_id))
                                    .map((lt) => ({ value: lt.leave_type_id, label: lt.leave_type }))[0] || null}
                                onChange={(opt) => setFormData({ ...formData, leave_type_id: opt ? opt.value : "" })}
                                isSearchable={false}
                                placeholder="Select Leave Type"
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Start Date <span style={{ color: "red" }}>*</span>
                            </label>
                            <DatePicker
                                selected={formData.start_date ? new Date(formData.start_date) : null}
                                onChange={(date) =>
                                    setFormData({
                                        ...formData,
                                        start_date: toLocalDate(date),
                                        end_date: ""
                                    })
                                }
                                minDate={oneMonthAgo()}
                                dateFormat="yyyy-MM-dd"
                                onMonthChange={(date) => fetchHolidays(date.getMonth() + 1, date.getFullYear())}
                                filterDate={(date) => !isWeekend(date) && !holidayDates.includes(date.toDateString()) && !bookedDates.has(date.toDateString())}
                                renderDayContents={renderDayContents}
                            />
                        </div>

                        <div className="form-group">
                            <label>Start Date Breakdown</label>
                            <Select
                                options={BREAKDOWN_OPTIONS}
                                value={BREAKDOWN_OPTIONS.find((o) => o.value === formData.start_date_breakdown) || BREAKDOWN_OPTIONS[0]}
                                onChange={(opt) => setFormData({ ...formData, start_date_breakdown: opt ? opt.value : "Full" })}
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                End Date <span style={{ color: "red" }}>*</span>
                            </label>
                            <DatePicker
                                selected={formData.end_date ? new Date(formData.end_date) : null}
                                onChange={(date) =>
                                    setFormData({
                                        ...formData,
                                        end_date: toLocalDate(date)
                                    })
                                }
                                minDate={formData.start_date ? new Date(formData.start_date) : oneMonthAgo()}
                                disabled={!formData.start_date}
                                dateFormat="yyyy-MM-dd"
                                onMonthChange={(date) => fetchHolidays(date.getMonth() + 1, date.getFullYear())}
                                filterDate={(date) => !isWeekend(date) && !holidayDates.includes(date.toDateString()) && !bookedDates.has(date.toDateString())}
                                renderDayContents={renderDayContents}
                                className={!formData.start_date ? "disabled-datepicker" : ""}
                            />
                        </div>

                        <div className="form-group">
                            <label>End Date Breakdown</label>
                            <Select
                                options={BREAKDOWN_OPTIONS}
                                value={BREAKDOWN_OPTIONS.find((o) => o.value === formData.end_date_breakdown) || BREAKDOWN_OPTIONS[0]}
                                onChange={(opt) => setFormData({ ...formData, end_date_breakdown: opt ? opt.value : "Full" })}
                                isSearchable={false}
                                isDisabled={isSingleDay}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                            />
                        </div>

                        <div className="form-group">
                            <label>Attachment</label>

                            {/* ✅ Show existing file with delete icon */}
                            {formData.old_attachment && !file && (
                                <div
                                    style={{
                                        marginBottom: "5px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        background: "#f7f7f7",
                                        borderRadius: "6px",
                                        padding: "5px 10px",
                                    }}
                                >
                                    <a
                                        href={`${API_BASE}/api/Leave/attachment/${editId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#4a90e2", fontSize: "0.9rem" }}
                                    >
                                        {attachmentName(formData.old_attachment)}
                                    </a>
                                    <i class="fa-solid fa-xmark"
                                        onClick={handleRemoveOldAttachment}
                                        title="Remove attachment"
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            color: "gray",
                                            fontWeight: "bold",
                                            cursor: "pointer",
                                            fontSize: "16px",
                                        }}
                                    ></i>
                                </div>
                            )}

                            <input
                                type="file"
                                ref={fileInputRef}
                                className="form-control"
                                accept=".jpg,.jpeg,.png,.pdf,.zip"
                                onChange={handleFileChange}
                            />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Description</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Description"
                                value={formData.leave_description}
                                onChange={(e) => setFormData({ ...formData, leave_description: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ visibility: "hidden" }}>Submit</label>
                            {editId ? (
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button className="btn btn-success w-100" onClick={handleSave}>
                                        Resubmit
                                    </button>
                                    <button className="btn btn-secondary w-100" onClick={handleCancelEdit}>
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button className="btn btn-success w-100" onClick={handleSave}>
                                    Submit
                                </button>
                            )}
                        </div>
                    </div>

                    <table className="leave-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Leave Type</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Attachment</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((item) => (
                                <tr key={item.leave_requests_id}>
                                    <td>{item.leave_requests_id}</td>
                                    <td>{item.leave_type}</td>
                                    <td>{formatDate(item.start_date)} ({item.start_date_breakdown || "Full"})</td>
                                    <td>{formatDate(item.end_date)} ({item.end_date_breakdown || "Full"})</td>
                                    <td style={{ textAlign: "center" }}>
                                        {item.attachment ? (
                                            <a
                                                href={`${API_BASE}/api/Leave/attachment/${item.leave_requests_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#4a90e2", fontSize: "18px" }}
                                                title={attachmentName(item.attachment)}
                                            >
                                                <FaPaperclip />
                                            </a>
                                        ) : (
                                            <span style={{ color: "#999" }}>No File</span>
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={`status-pill ${item.leave_status === "Canceled"
                                                ? "status-canceled"
                                                : item.leave_status === "Approved"
                                                    ? "status-approved"
                                                    : item.leave_status === "Rejected"
                                                        ? "status-rejected"
                                                        : "status-pending"
                                                }`}
                                        >
                                            {item.leave_status || "Pending"}
                                        </span>
                                    </td>
                                    <td>
                                        {item.leave_status === "Approved" ? (
                                            // Approved leave is locked — no cancel / edit / delete
                                            <span style={{ color: "#94a3b8" }}>—</span>
                                        ) : item.leave_status === "Canceled" || item.leave_status === "Rejected" ? (
                                            // Canceled / Rejected → edit and resubmit as a fresh request.
                                            // (Rejected can no longer be canceled — cancel only applies to Pending.)
                                            <>
                                                <button
                                                    className="btn btn-warning btn-sm"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm ms-2"
                                                    onClick={() => handleDelete(item.leave_requests_id)}
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-gray btn-sm"
                                                    onClick={() => handleCancel(item.leave_requests_id)}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="leave-summary-container">
                        <div className="leave-balance">
                            <h4>Leave Balance</h4>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Leave Type</th>
                                        <th>Paid?</th>
                                        <th>Max Leaves</th>
                                        <th>Used This Year</th>
                                        <th>Balance Leave</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaveBalance.map((item, i) => {
                                        // remaining_days_year is the year-scoped, approved-only
                                        // figure payroll uses. Once it hits 0 further leave of
                                        // this type is still allowed — it just becomes unpaid.
                                        const remaining = Number(item.remaining_days_year ?? item.balance_leave);
                                        return (
                                            <tr key={i}>
                                                <td>{item.leave_type}</td>
                                                <td>
                                                    <span className={`leave-paid-badge ${item.is_paid === false ? "is-unpaid" : "is-paid"}`}>
                                                        {item.is_paid === false ? "Unpaid" : "Paid"}
                                                    </span>
                                                </td>
                                                <td>{item.entitled_days ?? item.max_leaves}</td>
                                                <td>{item.used_days_year ?? "—"}</td>
                                                <td>
                                                    {item.balance_leave}
                                                    {remaining <= 0 && item.is_paid !== false && (
                                                        <span className="leave-lop-hint" title="You can still apply — the extra days will be unpaid">
                                                            further leave = unpaid
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="leave-history">
                            <h4>Leave History</h4>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Leave ID</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Leave Type</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaveHistory.map((item) => (
                                        <tr key={item.leave_id}>
                                            <td>{item.leave_id}</td>
                                            <td>{formatDate(item.start_date)}</td>
                                            <td>{formatDate(item.end_date)}</td>
                                            <td>{item.leave_type}</td>
                                            <td>{item.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MyLeaveRequests;
